import React from 'react';
import T from 'prop-types';
import {
  Navigation,
  Form,
  Checkbox,
  Field,
  FieldFeedback,
  Hint,
} from '@piggybank/form';
import {
  Heading,
  Paragraph,
  HorizontalRule,
  Link,
  ListItem,
  OrderedList,
  Section,
} from '@piggybank/core';
import {
  UNIT_TRUST_INVESTMENT_ACCOUNT,
  EQUITY_INVESTMENT_ACCOUNT,
} from 'srcAlias/client/enums/productDescriptionCode';
import {
  Translation,
  TranslationScope,
  Optional,
} from 'srcAlias/client/locale';
import { SD } from 'srcAlias/client/enums/crossBorderEligibiltyType';
import useTrackPageViewAnalytics from 'srcAlias/client/global/customHooks/useTrackPageViewAnalytics';
import translations from 'contentAlias';
import { get } from 'lodash-es';
import hooks from 'hooksAlias';
import { useSelector } from 'react-redux';
export const Declaration = ({ segmentName, pageName, productType }) => {
  //NOSONAR
  useTrackPageViewAnalytics('declaration');
  const initialValues = {
    checkboxDeclaration: false,
    checkboxRiskDisclosure: false,
    tncEquityCheckbox: false,
    tncUTCheckbox: false,
  };
  const FormFeedback = get(hooks, 'formfeedback.FormFeedbackHook', () => null);
  const declarationValidation = get(
    hooks,
    'validations.default.declarationSchema.declarationValidation',
    () => null
  );
  const validateData = get(
    hooks,
    'validations.default.declarationValidation.validateData',
    null
  );

  const GeneralInvestment = get(
    hooks,
    'customer.generalInvestmentContent.GeneralInvestment',
    () => null
  );
  const DeclarationContent = get(
    hooks,
    'customer.declarationContent.DeclarationContent',
    () => null
  );
  const DeclarationUrlContent = get(
    hooks,
    'customer.declarationUrlContent.DeclarationUrlContent',
    () => null
  );

  const ClientClosureDisclosureStatement = get(
    hooks,
    'customer.declarationContent.ClientClosureDisclosureStatement',
    () => null
  );

  const CrossBorderDisclosureStatement = get(
    hooks,
    'customer.declarationContent.CrossBorderDisclosureStatement',
    () => null
  );

  const crossBorderEligibilityType = useSelector((state) =>
    get(state, 'eligibility.responseData.data.crossBorderEligibilityType', '')
  );

  return (
    <TranslationScope path={`${segmentName}.${pageName}`}>
      <Heading level={1}>
        <Translation path="heading" />
      </Heading>
      <HorizontalRule />
      <>
        <Form
          initialValues={initialValues}
          name={'declarationDetails'}
          validationSchema={declarationValidation}
          validate={validateData ? (values) => validateData(values) : undefined}
          validateOnBlur
          validateOnChange
        >
          <FormFeedback />

          {productType === UNIT_TRUST_INVESTMENT_ACCOUNT ? (
            <Paragraph>
              <Paragraph>
                <Translation path="utSGProduct.firstLine" />
              </Paragraph>
              <Paragraph>
                <div>
                  <Link
                    href="https://www.hsbc.com.sg/content/dam/hsbc/sg/documents/investments/investment-terms.pdf"
                    external
                    textMap={{
                      newWindow: <Translation path="utSGProduct.textMap" />,
                    }}
                    name="termsAndConditionLink"
                  >
                    <Translation path="utSGProduct.link1Text" />
                  </Link>
                </div>
                <div>
                  <Link
                    href="https://www.hsbc.com.sg/content/dam/hsbc/sg/documents/investments/additional-terms-for-investment-fund-transactions.pdf"
                    external
                    textMap={{
                      newWindow: <Translation path="utSGProduct.textMap" />,
                    }}
                    name="termsAndConditionLink"
                  >
                    <Translation path="utSGProduct.link2Text" />
                  </Link>
                </div>
                <ClientClosureDisclosureStatement />
                {crossBorderEligibilityType === SD && (
                  <CrossBorderDisclosureStatement />
                )}
              </Paragraph>
              <Field name="tncUTCheckbox">
                <Checkbox>
                  <Translation path="utSGProduct.tncCheckbox" />
                </Checkbox>
                <FieldFeedback />
              </Field>
              <Optional>
                <Paragraph marginBottom={2}>
                  <Translation path="lawsAndRegulationsConfirmation" />
                </Paragraph>
              </Optional>
            </Paragraph>
          ) : (
            <>
              <Optional>
                <Paragraph>
                  <Translation path="otherInvestmentProducts.firstLine" />
                  <Link
                    href={
                      translations.customer.declaration.otherInvestmentProducts
                        .linkUrl
                    }
                    external
                    textMap={{
                      newWindow: (
                        <Translation path="otherInvestmentProducts.textMap" />
                      ),
                    }}
                    name="termsAndConditionLink"
                  >
                    <Translation path="otherInvestmentProducts.linkText" />
                  </Link>
                  <ClientClosureDisclosureStatement />
                  {crossBorderEligibilityType === SD && (
                    <CrossBorderDisclosureStatement />
                  )}
                  <Field name="tncEquityCheckbox">
                    <Checkbox>
                      <Translation path="otherInvestmentProducts.tncCheckbox" />
                    </Checkbox>
                    <FieldFeedback />
                  </Field>
                </Paragraph>
                <Paragraph marginBottom={2}>
                  <Translation path="lawsAndRegulationsConfirmation" />
                </Paragraph>
                {productType === EQUITY_INVESTMENT_ACCOUNT && (
                  <Paragraph>
                    <Translation path="otherInvestmentProducts.equitiesInformationText" />
                  </Paragraph>
                )}
              </Optional>
              <DeclarationUrlContent />
            </>
          )}

          <Heading level={2} accentBar>
            <Translation path="riskDisclosureHeading" />
          </Heading>

          <GeneralInvestment
            path={translations.customer.declaration.generalInvestmentContent}
          />

          <Optional>
            <Field name="checkboxRiskDisclosure">
              <Checkbox>
                <Paragraph>
                  <Translation path="checkboxRiskDisclosure.para0" />
                </Paragraph>
                <Paragraph>
                  <Translation path="checkboxRiskDisclosure.para1Start" />
                </Paragraph>
                <Paragraph>
                  <Translation path="checkboxRiskDisclosure.para1a" />
                </Paragraph>
                <Paragraph>
                  <Translation path="checkboxRiskDisclosure.para1b" />
                </Paragraph>

                <Paragraph>
                  <Translation path="checkboxRiskDisclosure.para2" />
                </Paragraph>
                <Paragraph>
                  <Translation path="checkboxRiskDisclosure.para3" />
                </Paragraph>
                <Paragraph>
                  <Translation path="checkboxRiskDisclosure.para3a" />
                </Paragraph>
                <Paragraph>
                  <Translation path="checkboxRiskDisclosure.para3b" />
                </Paragraph>
                <Paragraph>
                  <Translation path="checkboxRiskDisclosure.para3c" />
                </Paragraph>
                <Paragraph>
                  <Translation path="checkboxRiskDisclosure.para3d" />
                </Paragraph>
                <Paragraph>
                  <Translation path="checkboxRiskDisclosure.para3e" />
                </Paragraph>
                <Paragraph>
                  <Translation path="checkboxRiskDisclosure.para3f" />
                </Paragraph>
              </Checkbox>
              <FieldFeedback />
            </Field>
          </Optional>
          <Heading level={2} accentBar>
            <Translation path="declarationHeading" />
          </Heading>

          <Paragraph>
            <DeclarationContent
              path={translations.customer.declaration.declarationContent}
            />
          </Paragraph>

          <Field name="checkboxDeclaration">
            <Checkbox>
              <Translation path="checkboxDeclaration" />
              <Optional>
                <Hint>
                  <OrderedList marginBottom={0} type={'lower-roman'}>
                    {translations.customer.declaration.checkboxDeclarationHint.map(
                      (i, index) => (
                        <ListItem key={index}>
                          {i.length === 2 ? (
                            <>
                              {i[0]}
                              <Section marginBottom={3}>{i[1]}</Section>{' '}
                            </>
                          ) : (
                            i
                          )}
                        </ListItem>
                      )
                    )}
                  </OrderedList>
                </Hint>
              </Optional>
            </Checkbox>
            <FieldFeedback />
          </Field>
          <Paragraph></Paragraph>
          <Navigation textMap={translations.customer.declaration.textMap} />
        </Form>
      </>
    </TranslationScope>
  );
};

Declaration.propTypes = {
  segmentName: T.string,
  pageName: T.string,
  checkboxDeclaration: T.bool,
  checkboxRiskDisclosure: T.bool,
  declarationValidation: T.object,
  productType: T.string,
};
